import { z } from "zod";

// Pillar Variance Engine (Cockpit slice 3) — the LinkedIn format vocabulary and the angle
// ("mechanic") catalogue. Shared by the server (rotation, payload, callback validation), the
// client (labels) and — copied verbatim into the n8n writer prompts — the text generation.
// Keep `rule` in English (the writer prompts are English), `label` in German (UI).
// Phase 1 = text structures + one real photo; carousel/chart come once multi-image + Late
// document posts exist. Ids are stable API strings: never rename, only add.

export const PILLAR_FORMATS_VERSION = 1;

export const LINKEDIN_FORMATS = [
  {
    id: "text-hook",
    label: "Text",
    rule: "Plain-text post: a one-line contrarian hook, then 3-6 short lines that earn it. No bullet list, no dependence on an image.",
    needsImage: false,
  },
  {
    id: "list",
    label: "Liste",
    rule: "A one-line setup, then a numbered or dashed list of 3-7 crisp points, one line each, and a one-line close.",
    needsImage: false,
  },
  {
    id: "story",
    label: "Story",
    rule: "First-person mini story in 4-8 short paragraphs: the situation, the turn, the lesson. Concrete details, no moralising.",
    needsImage: false,
  },
  {
    id: "mini-case",
    label: "Mini-Case",
    rule: "Before / what we did / result, with at least one concrete number in the result. Short, factual, no superlatives.",
    needsImage: false,
  },
  {
    id: "comparison",
    label: "Vergleich",
    rule: "A contrast written as paired lines (then vs now, expectation vs reality, A vs B), 3-5 pairs, then one sentence on what the reader should do with it.",
    needsImage: false,
  },
  {
    id: "question",
    label: "Frage",
    rule: "Opens with a question the audience actually asks, answers it plainly in 3-6 lines, ends with one question back to the reader.",
    needsImage: false,
  },
  {
    id: "quote",
    label: "Zitat",
    rule: "One quotable sentence as the first line (the quote IS the hook), then 2-4 lines of context that make it land.",
    needsImage: false,
  },
  {
    id: "photo-hook",
    label: "Foto + Hook",
    rule: "Built around the supplied real photo: the first line says what the photo shows, the next lines make the point the photo proves. Never describe a photo that was not supplied.",
    needsImage: true,
  },
] as const;

export type FormatId = (typeof LINKEDIN_FORMATS)[number]["id"];
export const FORMAT_IDS = LINKEDIN_FORMATS.map((f) => f.id) as FormatId[];

/** DFY content profile: `agency` = the B2B LinkedIn engine (default), `hotel` = the "DFY Hotel" n8n workflow. */
export const DFY_PROFILES = ["agency", "hotel"] as const;
export type DfyProfile = (typeof DFY_PROFILES)[number];

/**
 * Hotel overrides of the format rules (Mike 2026-10-05): a hotel post never promises a metric, so
 * "mini-case" becomes a guest story without numbers. Copied verbatim into the FORMAT_RULES of the n8n
 * workflow "DFY Hotel" (node "DFY Input-Vorbereitung"); regenerate that map with formatRulesFor("hotel").
 */
export const HOTEL_FORMAT_RULE_OVERRIDES: Partial<Record<FormatId, string>> = {
  "mini-case": "Guest story (Gaestegeschichte): a short story about one guest moment, told only from the hotel's own facts block (brand house facts and the supplied topic). No invented facts, no numbers, no percentages, no before/after metrics. Situation, the moment, what it meant for the guest. Short, warm, factual, no superlatives.",
};

/** The FORMAT_RULES map (format id → writer rule) the n8n workflow of a profile embeds. */
export function formatRulesFor(profile: DfyProfile): Record<FormatId, string> {
  const overrides = profile === "hotel" ? HOTEL_FORMAT_RULE_OVERRIDES : {};
  return Object.fromEntries(LINKEDIN_FORMATS.map((f) => [f.id, overrides[f.id] ?? f.rule])) as Record<FormatId, string>;
}

export const PILLAR_MECHANICS = [
  { id: "fact", label: "Ein Fakt", rule: "one hard fact, number or date carries the post; everything else explains what it means for the reader" },
  { id: "person", label: "Ein Mensch", rule: "one person (a customer, a team member, the founder) is the subject; the fact sits in the background" },
  { id: "objection", label: "Ein Einwand", rule: "take one objection the audience really raises, state it fairly, then answer it" },
  { id: "occasion", label: "Ein Anlass", rule: "tie the pillar to a concrete date, season or event that is actually near; say when" },
  { id: "series-step", label: "Nächste Folge", rule: "continue the pillar as the next episode of a series; name it as part of the series" },
  { id: "mistake", label: "Ein Fehler", rule: "one specific mistake (ours or the industry's), what it cost, what changed" },
  { id: "behind-the-scenes", label: "Hinter den Kulissen", rule: "show one concrete step of how the work is actually done; no polish" },
  { id: "plan", label: "Mini-Plan", rule: "a short reproducible sequence of steps the reader can follow this week" },
] as const;

export type MechanicId = (typeof PILLAR_MECHANICS)[number]["id"];
export const MECHANIC_IDS = PILLAR_MECHANICS.map((m) => m.id) as MechanicId[];

export function formatLabel(id: string | null | undefined): string | null {
  if (!id) return null;
  return LINKEDIN_FORMATS.find((f) => f.id === id)?.label ?? id;
}

export function mechanicLabel(id: string | null | undefined): string | null {
  if (!id) return null;
  return PILLAR_MECHANICS.find((m) => m.id === id)?.label ?? id;
}

export function isFormatId(v: unknown): v is FormatId {
  return typeof v === "string" && (FORMAT_IDS as string[]).includes(v);
}

export function isMechanicId(v: unknown): v is MechanicId {
  return typeof v === "string" && (MECHANIC_IDS as string[]).includes(v);
}

// ── Slot plan (what the app hands n8n per weekly cycle) ─────────────────────
// Mirrors the 5 fixed slots the n8n "DFY Build Slots" node produces today (3 authority,
// 1 brand, 1 offer) so the lane keeps its writer routing; the app adds pillar + variance.

export type SlotPostType = "authority" | "brand" | "offer";

export const DEFAULT_SLOTS: { slot: string; postType: SlotPostType }[] = [
  { slot: "authority-1", postType: "authority" },
  { slot: "authority-2", postType: "authority" },
  { slot: "authority-3", postType: "authority" },
  { slot: "brand-1", postType: "brand" },
  { slot: "offer-1", postType: "offer" },
];

export const slotPlanEntrySchema = z.object({
  slot: z.string().min(1).max(40),
  postType: z.enum(["authority", "brand", "offer"]),
  pillarName: z.string().min(1).max(200),
  pillarType: z.string().min(1).max(50),
  format: z.enum(FORMAT_IDS as [FormatId, ...FormatId[]]),
  mechanic: z.enum(MECHANIC_IDS as [MechanicId, ...MechanicId[]]),
  /** recent titles for this pillar the writer must not repeat (max 12) */
  avoidTitles: z.array(z.string().max(200)).max(12),
  /** photo-hook only: the library upload the app attaches at ingest (n8n never fetches it — the
   *  /objects proxy is auth-gated) and the customer's note about the photo, the writer's only
   *  grounding for "what the photo shows" */
  imageUploadId: z.string().max(64).nullable().optional(),
  imageHint: z.string().max(200).nullable().optional(),
});
export type SlotPlanEntry = z.infer<typeof slotPlanEntrySchema>;
export const slotPlanSchema = z.array(slotPlanEntrySchema).min(1).max(10);
export type SlotPlan = z.infer<typeof slotPlanSchema>;

/** The snake_case shape n8n's Code nodes read (matches today's post_type/post_slot fields). */
export function toN8nSlot(entry: SlotPlanEntry) {
  return {
    post_type: entry.postType,
    post_slot: entry.slot,
    pillar_name: entry.pillarName,
    pillar_type: entry.pillarType,
    format: entry.format,
    mechanic: entry.mechanic,
    avoid_titles: entry.avoidTitles,
    image_hint: entry.imageHint ?? null,
    has_photo: !!entry.imageUploadId,
  };
}

// ── Media formats (Hotel-Edition B1, PLAN.md §7.1) ───────────────────────────────────────────────
// What KIND of post it is (photo, carousel, reel, LinkedIn text post) — independent of the LinkedIn
// writing `format` above. Stored in pillar_posts.media_format; NULL on older rows.
export const MEDIA_FORMATS = ["foto", "karussell", "reel", "linkedin"] as const;
export type MediaFormat = (typeof MEDIA_FORMATS)[number];

export function isMediaFormat(v: unknown): v is MediaFormat {
  return typeof v === "string" && (MEDIA_FORMATS as readonly string[]).includes(v);
}

/** Display format of a row: the stored media_format, else derived (platform linkedin → linkedin, else foto).
 *  Must match `effectiveMediaFormatSql` in server/repositories/contentRepo.ts. */
export function effectiveMediaFormat(p: { mediaFormat?: string | null; platform?: string | null }): MediaFormat {
  if (isMediaFormat(p.mediaFormat)) return p.mediaFormat;
  return p.platform === "linkedin" ? "linkedin" : "foto";
}

export const MEDIA_FORMAT_LABELS: Record<MediaFormat, string> = {
  foto: "Foto",
  karussell: "Karussell",
  reel: "Reel",
  linkedin: "LinkedIn-Beitrag",
};
